import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../../lib/site";

const NAV = [
  { label: "Features", to: "/#features" },
  { label: "Who it's for", to: "/#who" },
  { label: "Why RenewTracker", to: "/#why" },
  { label: "Pricing", to: "/#pricing" },
  { label: "FAQ", to: "/#faq" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/80 backdrop-blur-xl shadow-[0_4px_20px_rgba(15,23,42,0.04)]"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="RenewTracker" className="h-9 w-9 rounded-xl" />
          <span className="font-display text-lg font-700 font-bold tracking-tight text-ink">
            Renew<span className="text-brand">Tracker</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => handleNav(n.to)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="relative text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-brand"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            onClick={() => handleNav("/contact")}
            data-testid="header-cta-btn"
            className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
          >
            Get the RenewTracker App
          </button>
        </div>

        <button
          className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => handleNav(n.to)}
                className="rounded-xl px-3 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-50"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("/contact")}
              className="mt-2 rounded-full bg-brand px-5 py-3 text-center text-sm font-semibold text-white"
            >
              Get the RenewTracker App
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
